// src/pages/Admin/WebinarsManager.jsx
import React, { useEffect, useState } from "react";
import styled from "styled-components";

/* ===== API base ===== */
const API_BASE =
  (typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_API_BASE_URL) ||
  process.env.REACT_APP_API_BASE_URL ||
  "http://localhost:5000";

/* ===== Styled components ===== */
const Page = styled.div`
  display: grid;
  gap: 16px;
  color: #f1f1f1;
`;

const Title = styled.h2`
  margin: 0;
  font-weight: 800;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const Card = styled.div`
  background: #0f1b2d;
  border: 1px solid #1e293b;
  border-radius: 16px;
  padding: 16px;
`;

const TableWrap = styled.div`
  width: 100%;
  overflow: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 10px;

  thead th {
    text-align: left;
    color: #d6d9de;
    font-weight: 700;
    font-size: 0.9rem;
    padding: 0 10px;
    white-space: nowrap;
  }

  tbody td {
    background: #0b1320;
    border: 1px solid #1e293b;
    padding: 12px 10px;
    border-radius: 10px;
    vertical-align: top;
  }
`;

const Btn = styled.button`
  background: ${(p) =>
    p.variant === "ghost"
      ? "transparent"
      : "linear-gradient(45deg,#ff914d,#ff6b35)"};
  border: ${(p) => (p.variant === "ghost" ? "1px solid #334155" : "0")};
  color: #fff;
  border-radius: 10px;
  padding: 10px 12px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border-radius: 10px;
  border: 1px solid #1e293b;
  background: #0b1320;
  color: #f1f1f1;
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 10px;
  border-radius: 10px;
  border: 1px solid #1e293b;
  background: #0b1320;
  color: #f1f1f1;
  min-height: 110px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ModalBg = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  z-index: 2000;
`;

const Modal = styled.div`
  width: min(900px, 95vw);
  max-height: 90vh;
  overflow: auto;
  background: #0f1b2d;
  border: 1px solid #1e293b;
  border-radius: 16px;
  padding: 16px;
`;

const StatusPill = styled.span`
  display: inline-block;
  padding: 0.3rem 0.6rem;
  border-radius: 999px;
  font-weight: 800;
  font-size: 0.8rem;
  color: #0b1320;
  background: ${(p) => (p.variant === "upcoming" ? "#22c55e" : "#94a3b8")};
`;

const Subtle = styled.div`
  color: #94a3b8;
  font-size: 12px;
`;

const Muted = styled.span`
  color: #cbd5e1;
`;

const Empty = styled.div`
  color: #cbd5e1;
  padding: 14px;
`;

const SectionTitle = styled.h4`
  margin: 16px 0 8px;
  font-weight: 800;
  color: #e2e8f0;
`;

/* ===== Registrations modal polish ===== */
const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 0 0 12px;
`;

const ModalTitleText = styled.h3`
  margin: 0;
  font-weight: 800;
  color: #e2e8f0;
  font-size: 1.1rem;
`;

const ActionBar = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin: 4px 0 14px;
`;

const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: #0b1320;
  border: 1px solid #1e293b;
  color: #cbd5e1;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 700;
`;

const RegsTableWrap = styled.div`
  max-height: 60vh;
  overflow: auto;
  border-radius: 12px;
  border: 1px solid #1e293b;
`;

const RegsTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.95rem;

  thead th {
    position: sticky;
    top: 0;
    background: #0f1b2d;
    color: #cbd5e1;
    text-align: left;
    font-weight: 700;
    padding: 12px 14px;
    border-bottom: 1px solid #1e293b;
    z-index: 1;
    white-space: nowrap;
  }

  tbody td {
    padding: 12px 14px;
    border-bottom: 1px solid #132338;
    color: #e5edf7;
  }

  tbody tr:nth-child(even) td {
    background: #0c1728;
  }
  tbody tr:nth-child(odd) td {
    background: #0e1a2c;
  }
`;

/* ===== Helpers ===== */
function toDatetimeLocalValue(date) {
  if (!date) return "";
  const pad = (n) => String(n).padStart(2, "0");
  const d = new Date(date);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

/* ===== API helpers ===== */
function useApi() {
  async function list() {
    const r = await fetch(`${API_BASE}/api/webinars`, { credentials: "include" });
    return r.json();
  }
  async function create(payload) {
    const r = await fetch(`${API_BASE}/api/webinars`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      credentials: "include",
    });
    return r.json();
  }
  async function update(id, payload) {
    const r = await fetch(`${API_BASE}/api/webinars/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      credentials: "include",
    });
    return r.json();
  }
  async function remove(id) {
    const r = await fetch(`${API_BASE}/api/webinars/${id}`, {
      method: "DELETE",
      credentials: "include",
    });
    return r.json();
  }
  async function regs(id) {
    const r = await fetch(`${API_BASE}/api/webinars/${id}/registrations`, { credentials: "include" });
    return r.json();
  }
  function exportCsv(id) {
    window.location.href = `${API_BASE}/api/webinars/${id}/registrations/export.csv`;
  }
  return { list, create, update, remove, regs, exportCsv };
}

/* ===== Component ===== */
const initial = {
  title: "",
  topic: "",
  description: "",
  startsAt: "",
  durationMins: 60,
  timezone: "Asia/Kolkata",
  joiningLink: "",
  hostNames: "",
  // post-event
  highlights: "",
  recapHtml: "",
  recordingUrl: "",
  slideDeckUrl: "",
  isPublished: true,
};

export default function WebinarsManager() {
  const api = useApi();
  const [items, setItems] = useState([]);
  const [openEditor, setOpenEditor] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initial);
  const [signupsOf, setSignupsOf] = useState(null);
  const [signups, setSignups] = useState([]);

  async function load() {
    const res = await api.list();
    if (res?.ok) setItems(res.data || []);
  }
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(initial);
    setOpenEditor(true);
  };

  const openEdit = (it) => {
    setEditing(it);
    setForm({
      title: it.title || "",
      topic: it.topic || "",
      description: it.description || "",
      // 👇 keep local time in the input (no UTC shift)
      startsAt: it.startsAt ? toDatetimeLocalValue(it.startsAt) : "",
      durationMins: it.durationMins || 60,
      timezone: it.timezone || "Asia/Kolkata",
      joiningLink: it.joiningLink || "",
      hostNames: (it.hostNames || []).join(", "),
      // post-event
      highlights: it.highlights || "",
      recapHtml: it.recapHtml || "",
      recordingUrl: it.recordingUrl || "",
      slideDeckUrl: it.slideDeckUrl || "",
      isPublished: typeof it.isPublished === "boolean" ? it.isPublished : true,
    });
    setOpenEditor(true);
  };

  const save = async () => {
    const payload = {
      ...form,
      hostNames: form.hostNames
        ? form.hostNames.split(",").map((s) => s.trim()).filter(Boolean)
        : [],
      // Save as UTC (server-friendly), but display as local in the editor
      startsAt: form.startsAt ? new Date(form.startsAt).toISOString() : null,
    };
    const res = editing ? await api.update(editing._id, payload) : await api.create(payload);
    if (res?.ok) {
      setOpenEditor(false);
      await load();
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this webinar (and all signups)?")) return;
    const res = await api.remove(id);
    if (res?.ok) await load();
  };

  const viewRegs = async (it) => {
    const res = await api.regs(it._id);
    if (res?.ok) {
      setSignups(res.data || []);
      setSignupsOf(it);
    }
  };

  const copyEmails = () => {
    const txt = signups.map((s) => s.email).join(", ");
    window.navigator.clipboard.writeText(txt);
  };

  return (
    <Page>
      <Row>
        <Title>Webinars Manager</Title>
        <Btn onClick={openCreate}>+ New Webinar</Btn>
      </Row>

      <Card>
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <th>Title</th>
                <th>When</th>
                <th>Status</th>
                <th>Registrations</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    style={{ background: "transparent", border: 0, padding: 0 }}
                  >
                    <Empty>No webinars yet. Create your first one.</Empty>
                  </td>
                </tr>
              )}
              {items.map((it) => (
                <tr key={it._id}>
                  <td>
                    <div style={{ fontWeight: 700 }}>{it.title}</div>
                    <Subtle>{it.topic}</Subtle>
                  </td>
                  <td>
                    {new Date(it.startsAt).toLocaleString("en-IN", {
                      timeZone: it.timezone || "Asia/Kolkata",
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}{" "}
                    <Muted>({it.timezone || "Asia/Kolkata"})</Muted>
                  </td>
                  <td>
                    <StatusPill variant={it.status === "upcoming" ? "upcoming" : "past"}>
                      {it.status}
                    </StatusPill>
                  </td>
                  <td>
                    <Btn variant="ghost" onClick={() => viewRegs(it)}>
                      View
                    </Btn>
                  </td>
                  <td style={{ display: "flex", gap: 8 }}>
                    <Btn variant="ghost" onClick={() => openEdit(it)}>
                      Edit
                    </Btn>
                    <Btn variant="ghost" onClick={() => del(it._id)}>
                      Delete
                    </Btn>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      </Card>

      {/* ===== Editor Modal ===== */}
      {openEditor && (
        <ModalBg onClick={() => setOpenEditor(false)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>{editing ? "Edit webinar" : "Create webinar"}</h3>

            <SectionTitle>Event Details</SectionTitle>
            <Grid>
              <div>
                <label>Title</label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm((s) => ({ ...s, title: e.target.value }))}
                />
              </div>
              <div>
                <label>Topic</label>
                <Input
                  value={form.topic}
                  onChange={(e) => setForm((s) => ({ ...s, topic: e.target.value }))}
                />
              </div>
              <div>
                <label>Starts at (local)</label>
                <Input
                  type="datetime-local"
                  value={form.startsAt}
                  onChange={(e) => setForm((s) => ({ ...s, startsAt: e.target.value }))}
                />
              </div>
              <div>
                <label>Duration (mins)</label>
                <Input
                  type="number"
                  value={form.durationMins}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, durationMins: +e.target.value }))
                  }
                />
              </div>
              <div>
                <label>Timezone</label>
                <Input
                  value={form.timezone}
                  onChange={(e) => setForm((s) => ({ ...s, timezone: e.target.value }))}
                />
              </div>
              <div>
                <label>Joining link</label>
                <Input
                  value={form.joiningLink}
                  onChange={(e) => setForm((s) => ({ ...s, joiningLink: e.target.value }))}
                />
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <label>Description (HTML allowed)</label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))}
                />
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <label>Hosts (comma separated)</label>
                <Input
                  value={form.hostNames}
                  onChange={(e) => setForm((s) => ({ ...s, hostNames: e.target.value }))}
                />
              </div>
            </Grid>

            <SectionTitle>Post-event Details (for History)</SectionTitle>
            <Grid>
              <div>
                <label>Highlights (short)</label>
                <Input
                  value={form.highlights}
                  onChange={(e) => setForm((s) => ({ ...s, highlights: e.target.value }))}
                  placeholder="3 takeaways in one line"
                />
              </div>
              <div>
                <label>Recording URL</label>
                <Input
                  value={form.recordingUrl}
                  onChange={(e) => setForm((s) => ({ ...s, recordingUrl: e.target.value }))}
                  placeholder="https://youtube.com/..."
                />
              </div>
              <div>
                <label>Slides URL</label>
                <Input
                  value={form.slideDeckUrl}
                  onChange={(e) => setForm((s) => ({ ...s, slideDeckUrl: e.target.value }))}
                  placeholder="https://drive.google.com/..."
                />
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <label>Recap (HTML)</label>
                <Textarea
                  value={form.recapHtml}
                  onChange={(e) => setForm((s) => ({ ...s, recapHtml: e.target.value }))}
                  placeholder="<p>Full recap...</p>"
                />
              </div>
              <div>
                <label>Publish to History</label>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input
                    type="checkbox"
                    checked={!!form.isPublished}
                    onChange={(e) => setForm((s) => ({ ...s, isPublished: e.target.checked }))}
                  />
                  <span style={{ color: "#cbd5e1" }}>Visible on site</span>
                </div>
              </div>
            </Grid>

            <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
              <Btn onClick={save}>{editing ? "Save changes" : "Create"}</Btn>
              <Btn variant="ghost" onClick={() => setOpenEditor(false)}>
                Cancel
              </Btn>
            </div>
          </Modal>
        </ModalBg>
      )}

      {/* ===== Registrations Modal (roomier) ===== */}
      {signupsOf && (
        <ModalBg
          onClick={() => {
            setSignupsOf(null);
            setSignups([]);
          }}
        >
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitleText>Registrations — {signupsOf.title}</ModalTitleText>
              <CountBadge>{signups.length} total</CountBadge>
            </ModalHeader>

            <ActionBar>
              <Btn onClick={copyEmails}>Copy emails</Btn>
              <Btn onClick={() => api.exportCsv(signupsOf._id)}>Export CSV</Btn>
              <Btn
                variant="ghost"
                onClick={() => {
                  setSignupsOf(null);
                  setSignups([]);
                }}
              >
                Close
              </Btn>
            </ActionBar>

            <RegsTableWrap>
              <RegsTable>
                <thead>
                  <tr>
                    <th>S. No.</th>
                    <th>First Name</th>
                    <th>Last Name</th>
                    <th>Email</th>
                    <th>Consent</th>
                    <th>Date Filled</th>
                  </tr>
                </thead>
                <tbody>
                  {signups.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        style={{
                          padding: "16px",
                          color: "#cbd5e1",
                          background: "#0f1b2d",
                        }}
                      >
                        No registrations yet.
                      </td>
                    </tr>
                  ) : (
                    signups.map((s, i) => (
                      <tr key={s._id}>
                        <td>{i + 1}</td>
                        <td>{s.firstName || ""}</td>
                        <td>{s.lastName || ""}</td>
                        <td>{s.email}</td>
                        <td>{s.consent ? "Yes" : "No"}</td>
                        <td>
                          {new Date(s.createdAt).toLocaleString("en-IN", {
                            timeZone: "Asia/Kolkata",
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </RegsTable>
            </RegsTableWrap>
          </Modal>
        </ModalBg>
      )}
    </Page>
  );
}
